import { useCallback, useEffect, useState } from 'react';

/**
 * Маршрутизация кабинета.
 *
 * Своя, на History API, вместо библиотеки: экранов немного, вложенности нет, а
 * состояние фильтров всё равно живёт в строке запроса — и именно это нужно от
 * маршрутизатора. Пагинация в кабинете курсорная, и адрес вида `?limit=15&offset=NaN`,
 * который мы видели у Noor, здесь невозможен по построению.
 */

export interface Route {
  readonly section: string;
  readonly params: URLSearchParams;
}

const DEFAULT_SECTION = 'flow';

export function parseRoute(url: string): Route {
  const parsed = new URL(url, window.location.origin);
  const section = parsed.pathname.replace(/^\/+|\/+$/g, '');

  return { section: section === '' ? DEFAULT_SECTION : section, params: parsed.searchParams };
}

export function useRoute(): {
  readonly route: Route;
  readonly navigate: (
    section: string,
    params?: Record<string, string | undefined>,
    options?: { readonly replace?: boolean },
  ) => void;
  readonly setParams: (params: Record<string, string | undefined>) => void;
} {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.href));

  useEffect(() => {
    const onPop = (): void => setRoute(parseRoute(window.location.href));

    // Кнопка «назад» браузера обязана работать: диспетчер открывает карточку и
    // возвращается в очередь десятки раз за смену.
    window.addEventListener('popstate', onPop);

    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const push = useCallback(
    (section: string, params: Record<string, string | undefined>, replace = false): void => {
      const search = new URLSearchParams();

      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') {
          search.set(key, value);
        }
      }

      const query = search.toString();
      const url = `/${section}${query === '' ? '' : `?${query}`}`;

      // Переход со старого адреса заменяет запись истории: иначе «назад» вернёт на старый
      // адрес, тот снова переведёт вперёд, и из раздела не выйти.
      if (replace) {
        window.history.replaceState(null, '', url);
      } else {
        window.history.pushState(null, '', url);
      }

      setRoute(parseRoute(url));
    },
    [],
  );

  const navigate = useCallback(
    (
      section: string,
      params: Record<string, string | undefined> = {},
      options: { readonly replace?: boolean } = {},
    ) => push(section, params, options.replace === true),
    [push],
  );

  const setParams = useCallback(
    (params: Record<string, string | undefined>) => {
      const merged: Record<string, string | undefined> = {};

      route.params.forEach((value, key) => {
        merged[key] = value;
      });

      push(route.section, { ...merged, ...params });
    },
    [push, route],
  );

  return { route, navigate, setParams };
}
